import { FormCheckbox as GelFormCheckbox } from '@snsw/react-component-library';
import { type ReactNode } from 'react';
import { type FieldPath, type FieldValues, useController } from 'react-hook-form';
import { useFieldErrorMessage } from './hooks';
import type { FormFieldProps } from './types';

interface FormSwitchProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends FormFieldProps<TFieldValues, TName> {
  label: string | ReactNode;
  className?: string;
  margin?: { top: number | 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' };
  disabled?: boolean;
  isRequired?: boolean;
  isOptional?: boolean;

  /** An inverse checkbox will send the value 'false' when checked */
  inverse?: boolean;
}

export const FormSwitch = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: FormSwitchProps<TFieldValues, TName>) => {
  const { id, name, inverse, rules, errorMode, shouldUnregister, ...switchProps } = props;

  const {
    field: { ref, name: fieldName, onChange, value: formValue, ...fieldProps },
    fieldState,
  } = useController({
    name,
    rules,
    shouldUnregister,
  });

  const errorMessage = useFieldErrorMessage(fieldName, errorMode);

  const valueWhenChecked = !inverse;
  const isChecked = formValue === valueWhenChecked;

  const handleChange = () => {
    const nextChecked = !isChecked;
    const nextValue = nextChecked ? valueWhenChecked : !valueWhenChecked;

    onChange(nextValue);
  };

  return (
    <GelFormCheckbox
      {...fieldProps}
      {...switchProps}
      id={id}
      name={fieldName}
      inputRef={ref}
      value={valueWhenChecked.toString()}
      checked={isChecked}
      onChange={handleChange}
      hasError={fieldState.error != null}
      errorMessage={errorMessage}
    />
  );
};

/**
 * An alias for a single checkbox which is used as a switch to represent a true/false/indeterminate state.
 */
export const FormCheckbox = FormSwitch;
